import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Link, Skeleton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import { useRoutesHelper } from '../../helpers/routesHelper';
import Constants from '../../store/constants';
import { EventInformationType } from '../../core/models/events';

type StyleProps = {
  compact?: boolean;
};

const useStyles = makeStyles<StyleProps, 'cardImage' | 'gradientImg'>()((theme, { compact }, classes) => ({
  grid: {
    marginTop: 24,
  },
  card: {
    cursor: 'pointer',
    marginBottom: 32,
    ...(compact && {
      marginBottom: 20,
    }),
  },
  imageWrapper: {
    position: 'relative',
    '&:hover': {
      [`& .${classes.cardImage}, .${classes.gradientImg}`]: {
        filter: 'brightness(60%)',
      },
    },
  },
  cardImage: {
    width: '100%',
    height: '320px',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0px 8px 15px rgb(0 0 0 / 45%)',
    display: 'block',
    ...(compact && {
      height: 'auto',
      aspectRatio: '1 / 1',
    }),
  },
  gradientImg: {
    position: 'absolute',
    width: '100%',
    height: '120px',
    left: '0px',
    bottom: '0px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
    opacity: 0.7,
    borderRadius: '0px 0px 8px 8px',
    ...(compact && {
      height: 80,
    }),
  },
  linkStyle: {
    '&:hover': {
      color: 'rgb(178, 178, 178)',
      filter: 'brightness(60%)',
    },
  },
  titleText: {
    fontSize: 20,
    lineHeight: '22px',
    marginTop: 12,
    marginBottom: 6,
    ...(compact && {
      fontSize: 14,
      lineHeight: '16px',
      marginTop: 10,
      marginBottom: 4,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }),
  },
  subTitleText: {
    color: '#6B6B6B',
    ...(compact && {
      fontSize: 11,
      lineHeight: '14px',
    }),
  },
  skeletonImage: {
    width: '100%',
    height: '320px',
    borderRadius: '8px',
    ...(compact && {
      height: 'auto',
      aspectRatio: '1 / 1',
    }),
  },
  skeletonText: {
    height: 22,
    marginTop: 12,
    ...(compact && {
      height: 16,
      marginTop: 10,
    }),
  },
  sentinel: {
    width: '100%',
    height: 1,
  },
  emptyText: {
    color: '#6B6B6B',
    textAlign: 'center',
    padding: '40px 0',
  },
}));

const PAGE_SIZE = 12;

const GridSkeleton: FC<{ count: number; classes: any; itemXs: 4 | 6 | 12 }> = ({ count, classes, itemXs }) => (
  <>
    {Array.from(new Array(count)).map((_, index) => (
      <Grid item key={index} xs={itemXs} sm={6} md={4} className={classes.card}>
        <Skeleton animation="wave" variant="rectangular" className={classes.skeletonImage} />
        <Skeleton animation="wave" variant="rectangular" className={classes.skeletonText} />
        <Skeleton animation="wave" variant="rectangular" width="35%" className={classes.skeletonText} />
      </Grid>
    ))}
  </>
);

export const EventsGrid: FC<EventsGridPropsType> = memo(
  ({ load, reloadDependencyValue, pageSize = PAGE_SIZE, itemXs = 12, compact = false }) => {
  const { classes } = useStyles({ compact });
  const { getEventRoute, handleEventLink } = useRoutesHelper();

  const [events, setEvents] = useState<EventInformationType[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadInitial = async () => {
    loadingRef.current = true;
    setLoading(true);
    setHasMore(true);
    const res = await load(0);
    setEvents(res ?? []);
    setHasMore((res?.length ?? 0) >= pageSize);
    setLoading(false);
    loadingRef.current = false;
  };

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);
    const res = await load(events.length);
    setEvents((prev) => [...prev, ...(res ?? [])]);
    setHasMore((res?.length ?? 0) >= pageSize);
    setLoadingMore(false);
    loadingRef.current = false;
  }, [hasMore, events.length, load, pageSize]);

  useEffect(() => {
    loadInitial();
  }, [reloadDependencyValue]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isLoading || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, isLoading, hasMore]);

  return (
    <Grid container direction="row" spacing={3} className={classes.grid}>
      {isLoading && <GridSkeleton count={6} classes={classes} itemXs={itemXs} />}

      {!isLoading &&
        events.map((item) => (
          <Grid item key={item.slug} xs={itemXs} sm={6} md={4} className={classes.card}>
            <Link
              underline="none"
              onClick={() => handleEventLink(item.domainTypes, getEventRoute(item.slug))}
              className={classes.linkStyle}
            >
              <div className={classes.imageWrapper}>
                <img src={item.posterUrl} alt={item.title} className={classes.cardImage} loading="lazy" />
                <div className={classes.gradientImg} />
              </div>
              <Typography className={classes.titleText}>{item.title}</Typography>
              <Typography variant="caption" className={classes.subTitleText}>
                {`${dayjs(item.eventDate).format(Constants.DateFormat)}, ${item.city}`}
              </Typography>
            </Link>
          </Grid>
        ))}

      {isLoadingMore && <GridSkeleton count={3} classes={classes} itemXs={itemXs} />}

      {!isLoading && events.length === 0 && (
        <Grid item xs={12}>
          <Typography className={classes.emptyText}>Нет доступных мероприятий</Typography>
        </Grid>
      )}

      {!isLoading && hasMore && (
        <Grid item xs={12}>
          <div ref={sentinelRef} className={classes.sentinel} />
        </Grid>
      )}
    </Grid>
  );
  }
);

type EventsGridPropsType = {
  load: (skip: number) => Promise<EventInformationType[]>;
  reloadDependencyValue?: any;
  pageSize?: number;
  itemXs?: 4 | 6 | 12;
  compact?: boolean;
};
